import { z } from 'zod';

import { GitHubCoverageSchema } from './github-coverage';

// ---------------------------------------------------------------------------
// "Repos in each org" — the GitHub App POC contract. When an org has installed
// the App, we list the repositories the installation can see (the App's
// installation-scoped `GET /installation/repositories`, NOT the per-user
// `/user/repos`). The response is a discriminated union on `connected` so the
// "App not installed" state is distinct from "installed but zero repos", and it
// carries `total` + `truncated` (from GitHub's `total_count`) so a paginated
// truncation is never mistaken for a small org. Upstream failures are surfaced
// as HTTP 502/503 by the route, never as an empty list.
// ---------------------------------------------------------------------------

/** Whether the App was installed on all repos or an admin-selected subset. */
export const RepositorySelectionSchema = z.union([
  z.literal('all'),
  z.literal('selected'),
]);
export type RepositorySelection = z.infer<typeof RepositorySelectionSchema>;

/** One repository the installation can access (presentation-neutral DTO). */
export const InstallationRepoSchema = z.object({
  id: z.number(),
  fullName: z.string(),
  name: z.string(),
  owner: z.string(),
  private: z.boolean(),
  htmlUrl: z.string(),
});
export type InstallationRepo = z.infer<typeof InstallationRepoSchema>;

/** The `GET /api/github/org-repos` 200 body. */
export const OrgReposResponseSchema = z
  .discriminatedUnion('connected', [
    z.object({ connected: z.literal(false) }),
    z.object({
      connected: z.literal(true),
      // Legacy cached responses predate explicit provenance; this endpoint was
      // App-only then, so the only truthful migration default is App/full.
      source: z.enum(['app', 'pat']).default('app'),
      coverage: GitHubCoverageSchema.default({
        source: 'app',
        completeness: 'full',
        reasons: [],
      }),
      // Present on the live endpoint; optional keeps older cached/fixture payloads
      // readable while clients roll forward.
      installationId: z.number().int().positive().optional(),
      repositorySelection: RepositorySelectionSchema.optional(),
      repos: z.array(InstallationRepoSchema),
      /** GitHub's `total_count` — the true number the installation can access. */
      total: z.number(),
      /** True when pagination was capped, so `repos.length < total`. */
      truncated: z.boolean(),
    }),
  ])
  .superRefine((value, context) => {
    if (value.connected && value.source !== value.coverage.source) {
      context.addIssue({
        code: 'custom',
        message: 'Repository source and coverage source must match',
        path: ['coverage', 'source'],
      });
    }
  });
export type OrgReposResponse = z.infer<typeof OrgReposResponseSchema>;

/** Every non-success payload returned by the organization-repositories route. */
export const OrgReposErrorResponseSchema = z.object({
  error: z.enum([
    'unauthenticated',
    'org_required',
    'invalid_org',
    'payment_required',
    'no_token',
    'invalid_credential',
    'unconfigured',
    'upstream',
  ]),
});
export type OrgReposError = z.infer<
  typeof OrgReposErrorResponseSchema
>['error'];
